import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

import { markChunkReloadAttempt } from '@/lib/chunkReload';

// R413 — 旗標判準搬到 `lib/chunkReload.ts`(Firebase / 語言字典的動態載入也要用同一份);
// 舊名照樣從這裡匯出。
export { markChunkReloadAttempt };

/**
 * lazyWithReload — 跨部署後 lazy chunk 404 的自癒(R225)
 * =============================================================================
 *
 * **實際事故**(Bryan 2026-08-11 dev 回報「看不到檢查報告」):
 *
 * ```text
 * Unexpected Application Error!
 * Failed to fetch dynamically imported module:
 *   https://dev.creative.haitechservice.com/assets/CheckReportPage-QiiN3LfL.js
 * ```
 *
 * 當場量測三條:
 *   - `CheckReportPage-QiiN3LfL.js` → **404**(舊 build 的 hash)
 *   - 現行 `index-DSOdCOm1.js` 指向的 `CheckReportPage-CwcJ3VLU.js` → **200 / 37KB** ✅
 *   - 那個 404 還帶著 `cache-control: public, immutable, max-age=31536000`
 *
 * 所以**部署是好的**,壞的是「分頁裡跑的主 bundle 是舊的」:`/assets/*` 是 hash 檔名 +
 * immutable 一年快取,主 bundle 裡**寫死了當時那批 chunk 的檔名**。dev 換 image 之後,
 * 舊 chunk 檔在新 image 裡並不存在 → 舊分頁一旦走到 lazy route 就 404。
 *
 * 這不是罕見情況:dev 一天可能部好幾次,而使用者的分頁常開著好幾小時。任何 code-split
 * 的 SPA 都有這個結構性問題,標準解法就是**偵測到 chunk 載入失敗 → 強制重載一次**:
 * `index.html` 是 `no-store`(nginx),reload 就會拿到新的主 bundle → 新 chunk 名 → 自癒。
 *
 * ## 為什麼一個 session 只重載一次
 *
 * 無條件重載會變成無限迴圈:如果 chunk 是**真的壞了**(build 缺檔、CDN 掉檔、離線),
 * 每次重載都會再失敗、再重載,使用者看到的是永遠閃爍的白畫面,而且拿不到任何錯誤訊息。
 * 用 `sessionStorage` 記一次:第一次失敗 → 重載(絕大多數的跨部署情況在這裡就好了);
 * 第二次同一支還是失敗 → 把錯誤丟出去,讓 router 的錯誤畫面顯示真相。
 *
 * `sessionStorage` 不可用(隱私模式 / sandbox)→ 不重載,直接讓錯誤浮出來(寧可看得到
 * 錯誤,也不要在讀不到旗標的環境裡無限重載)。
 *
 * ⚠️ 另一半修在 nginx:`location /assets/` 的 `Cache-Control` 原本帶 `always`,**連 404
 * 都被冠上一年 immutable** → 那個 404 會被瀏覽器記一年,連 reload 都救不回來。R225 同輪
 * 拿掉了那個 `always`(見 `nginx.conf.template`)。兩邊要一起才完整。
 */

/**
 * `React.lazy` 的替代品:chunk 載入失敗時先強制重載一次,再失敗才把錯誤丟出去。
 *
 * @param chunkName 只用來當 sessionStorage 旗標的鍵(每支 route 各自獨立,一支壞掉不會
 *                  用掉別支的重載額度)。
 * @param factory   原本傳給 `React.lazy` 的那個 `() => import(...)`。
 */
export function lazyWithReload(
  chunkName: string,
  factory: () => Promise<{ default: ComponentType }>,
): LazyExoticComponent<ComponentType> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (markChunkReloadAttempt(chunkName)) {
        console.warn('[lazyWithReload] chunk load failed — reloading once for a fresh bundle', {
          chunkName,
          err,
        });
        window.location.reload();
        // reload 進行中:回一個永不 resolve 的 promise,讓 Suspense fallback 撐著,
        // 不要先閃一下錯誤畫面再跳走。
        return new Promise<{ default: ComponentType }>(() => {});
      }
      throw err;
    }
  });
}
